import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { logger } from 'src/logger';
import { generateOrgCards, GenerateOrgCardsParams } from 'src/api/baseball-card-service';

interface UseOrgCardGenerationArgs {
  /**
   * A stable string fingerprint of the selections that define a run (leader, view mode, chosen
   * employees, categories, root folder). When it drifts from the signature captured at submit
   * time, the accepted `jobId` is cleared so the UI re-offers "Generate" against the new inputs.
   */
  selectionSignature: string;
}

interface UseOrgCardGenerationReturn {
  /** True while the start-generation request is in flight. */
  isSubmitting: boolean;
  /** The accepted job id once the run has been started, else null. */
  jobId: string | null;
  /** A user-facing error message if starting the run failed, else null. */
  error: string | null;
  /** Starts the server-side org-card generation run for the given parameters. */
  submit: (params: GenerateOrgCardsParams) => Promise<void>;
  /** Clears the jobId/error so the step can be re-submitted. */
  reset: () => void;
}

/**
 * Owns the "start org-card generation" side-effect. Unlike the previous client-side PDF
 * builder, this fires a single async request to the backend, which runs a Step Functions
 * job that generates the cards into WorkDocs and emails the submitter on completion. The
 * hook holds only the submit lifecycle: in-flight flag, the returned job id, and any error.
 */
export const useOrgCardGeneration = ({ selectionSignature }: UseOrgCardGenerationArgs): UseOrgCardGenerationReturn => {
  const isMounted = useIsMounted();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The live signature, mirrored into a ref so `submit` can stamp the run's signature at submit
  // time without taking `selectionSignature` as a useCallback dep (which would churn `submit`'s
  // identity on every selection change and ripple through the container's memos).
  const latestSignatureRef = useRef(selectionSignature);
  latestSignatureRef.current = selectionSignature;
  // The signature the currently-accepted jobId was generated under, for staleness detection.
  const submittedSignatureRef = useRef<string | null>(null);

  const submit = useCallback(
    async (params: GenerateOrgCardsParams) => {
      try {
        setIsSubmitting(true);
        setError(null);
        setJobId(null);

        const response = await generateOrgCards(params);

        if (!isMounted.current) return;

        if (response) {
          setJobId(response.job_id);
          // Remember what this run was generated from so a later selection change marks it stale.
          submittedSignatureRef.current = latestSignatureRef.current;
        } else {
          // The service already logged the underlying cause (without the token).
          setError('Failed to start card generation. Please try again.');
        }
      } catch (err) {
        logger.error('Failed to start org-card generation');
        if (isMounted.current) setError('Failed to start card generation. Please try again.');
      } finally {
        if (isMounted.current) setIsSubmitting(false);
      }
    },
    [isMounted]
  );

  // Auto-invalidate a completed run when the selections change: once the user edits employees,
  // categories, leader, or view mode after generating, the accepted jobId no longer matches
  // what's on screen, so clear it (and any error) to re-offer "Generate". No loop — clearing
  // jobId re-runs this effect, but the `jobId !== null` guard is then false.
  useEffect(() => {
    if (jobId !== null && selectionSignature !== submittedSignatureRef.current) {
      setJobId(null);
      setError(null);
    }
  }, [selectionSignature, jobId]);

  const reset = useCallback(() => {
    setJobId(null);
    setError(null);
  }, []);

  return { isSubmitting, jobId, error, submit, reset };
};
